'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

const LOCK_PATH =
  'M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm-3 8V7a3 3 0 0 1 6 0v3H9Zm3 4a1.75 1.75 0 0 1 1 3.18V19a1 1 0 1 1-2 0v-1.82A1.75 1.75 0 0 1 12 14Z';

function getPasswordChecks(pwd) {
  const p = pwd || '';
  const length = p.length >= 8 && p.length <= 72;
  const lowercase = /[a-z]/.test(p);
  const uppercase = /[A-Z]/.test(p);
  const number = /\d/.test(p);
  const strength = length && lowercase && uppercase && number;
  return { length, lowercase, uppercase, number, strength };
}

function maskEmail(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) return '';
  const [local, domain] = email.split('@');
  const visible = local.slice(0, 2);
  return `${visible}***@${domain}`;
}

const CheckIcon = ({ ok }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" style={{ flexShrink: 0 }} aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill={ok ? '#0e813a' : '#D82121'} />
    {ok ? (
      <path d="m7.5 12.5 2.8 2.8L16.5 9" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M9 9l6 6M15 9l-6 6" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

const EyeIcon = ({ visible }) =>
  visible ? (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" width="20" height="20" aria-hidden="true">
      <g>
        <path fill="currentColor" d="M26.166 15.653a15.515 15.515 0 0 0-4.316-3.994l2.67-2.329a.625.625 0 1 0-.837-.928l-16 14.269a.624.624 0 0 0-.045.882.615.615 0 0 0 .336.194.624.624 0 0 0 .546-.147l3.053-2.679A9.963 9.963 0 0 0 16 22.084c5.254 0 9.936-5.447 10.135-5.681a.622.622 0 0 0 .031-.75Zm-7.991-.683a2.192 2.192 0 0 1 .309 1.1 2.4 2.4 0 0 1-2.468 2.33 2.549 2.549 0 0 1-1.267-.341Zm-1.984 5.874a7.677 7.677 0 0 1-3.621-.822l1.222-1.1a3.776 3.776 0 0 0 2.224.729 3.653 3.653 0 0 0 3.718-3.575 3.46 3.46 0 0 0-.612-1.957l1.748-1.575a14.79 14.79 0 0 1 3.983 3.431c-1.201 1.274-4.823 4.754-8.662 4.869Z" />
        <path fill="currentColor" d="M10.242 19.5a.593.593 0 0 0-.055-.876 19.479 19.479 0 0 1-3.006-2.616c1.225-1.3 4.922-4.839 8.82-4.839a12.826 12.826 0 0 1 2.754.408.669.669 0 0 0 .706-.359.573.573 0 0 0-.279-.764A9.226 9.226 0 0 0 16 9.925c-5.277 0-9.937 5.447-10.133 5.679a.623.623 0 0 0 0 .8 21.308 21.308 0 0 0 3.476 3.15.679.679 0 0 0 .899-.054Z" />
        <path fill="currentColor" d="M16.464 13.825a.625.625 0 1 0 .379-1.191 3.78 3.78 0 0 0-3.263.784 3.567 3.567 0 0 0-1.21 3.163.626.626 0 0 0 .615.514.6.6 0 0 0 .616-.736 2.3 2.3 0 0 1 .794-1.994 2.525 2.525 0 0 1 2.069-.54Z" />
      </g>
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
      <g>
        <path d="M16.256 12.494a3.578 3.578 0 1 0 3.718 3.575 3.652 3.652 0 0 0-3.718-3.575Zm0 5.9a2.33 2.33 0 1 1 2.468-2.325 2.402 2.402 0 0 1-2.468 2.325Z" fill="currentColor" />
        <path d="M16.256 14.133a.625.625 0 0 0 0 1.25.689.689 0 1 1-.749.686.625.625 0 1 0-1.25 0 2 2 0 1 0 2-1.936Z" fill="currentColor" />
        <path d="M26.405 15.648c-.16-.233-4.01-5.728-10.165-5.728-5.276 0-9.936 5.448-10.132 5.68a.622.622 0 0 0 0 .8c.196.233 4.856 5.68 10.133 5.68 5.253 0 9.936-5.447 10.134-5.68a.62.62 0 0 0 .03-.752Zm-9.974 5.191h-.19C12.354 20.84 8.649 17.3 7.42 16c1.226-1.3 4.923-4.839 8.82-4.839 4.553 0 7.812 3.517 8.853 4.807-1.201 1.276-4.823 4.756-8.662 4.871Z" fill="currentColor" />
      </g>
    </svg>
  );

export default function PasswordSetupGate() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const primaryRef = useRef(null);

  const passwordChecks = getPasswordChecks(password);

  const checkUser = () => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return;
      const user = JSON.parse(raw);
      if (user && user.passwordSet === false) {
        setEmail(user.email || '');
        setOpen(true);
      }
    } catch {
    }
  };

  useEffect(() => {
    checkUser();
    window.addEventListener('storage', checkUser);
    window.addEventListener('user-updated', checkUser);
    return () => {
      window.removeEventListener('storage', checkUser);
      window.removeEventListener('user-updated', checkUser);
    };
  }, []);

  useEffect(() => {
    const isVisible = open || successOpen;
    if (!isVisible) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    primaryRef.current?.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open, successOpen]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!passwordChecks.strength) {
      setError('Bitte erfülle alle Passwortanforderungen.');
      setPasswordFocused(true);
      return;
    }
    if (!confirm) {
      setError('Bitte bestätige dein Passwort.');
      return;
    }
    if (password !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setSubmitting(true);
    try {
      const jwt = localStorage.getItem('jwt');
      await axios.put(
        `${process.env.NEXT_PUBLIC_API_URL}/user/set-password`,
        { password },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      const raw = localStorage.getItem('user');
      const user = raw ? JSON.parse(raw) : {};
      user.passwordSet = true;
      localStorage.setItem('user', JSON.stringify(user));

      setPassword('');
      setConfirm('');
      setOpen(false);
      setSuccessOpen(true);
    } catch (err) {
      console.error('set-password failed:', err?.response?.status, err?.response?.data || err);
      setError('Fehler beim Speichern. Bitte versuche es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  if ((!open && !successOpen) || typeof document === 'undefined') return null;

  const stop = (event) => event.stopPropagation();

  return createPortal(
    <>
      {open && (
        <>
          <div className="psg-overlay" />

          <div
            className="psg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="psg-title"
            onClick={stop}
          >
            <div className="psg-head">
              <svg className="psg-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#0f4c8a" d={LOCK_PATH} />
              </svg>
              <h3 id="psg-title" className="psg-title">Passwort festlegen</h3>
              <p className="psg-subtitle">
                Du hast dich mit Google{email ? <> (<strong>{maskEmail(email)}</strong>)</> : ''} angemeldet.
                Lege jetzt ein Passwort fest, damit du dich künftig auch direkt mit deiner
                E-Mail-Adresse anmelden kannst.
              </p>
            </div>

            <div className="psg-body">
              <form onSubmit={handleSubmit} noValidate>
                <div className="psg-input-wrap">
                  <input
                    ref={primaryRef}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Neues Passwort"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    autoComplete="new-password"
                    disabled={submitting}
                  />
                  <button
                    className="psg-password-toggle"
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={submitting}
                    aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  >
                    <EyeIcon visible={showPassword} />
                  </button>
                </div>

                {(passwordFocused || password) && (
                  <div id="password-instructions" role="region" aria-live="polite" className="psg-strength">
                    <div className="psg-strength-label">Passwortstärke</div>
                    <div className="psg-strength-bar">
                      <div
                        className="psg-strength-bar-fill"
                        style={{
                          width: `${
                            ([passwordChecks.length, passwordChecks.lowercase, passwordChecks.uppercase, passwordChecks.number].filter(Boolean).length / 4) * 100
                          }%`,
                          background:
                            [passwordChecks.length, passwordChecks.lowercase, passwordChecks.uppercase, passwordChecks.number].filter(Boolean).length === 4
                              ? '#0e813a'
                              : [passwordChecks.length, passwordChecks.lowercase, passwordChecks.uppercase, passwordChecks.number].filter(Boolean).length >= 2
                              ? '#e0a800'
                              : '#D82121',
                        }}
                      />
                    </div>
                    <div className="psg-strength-scale">
                      <span>nicht sicher</span>
                      <span>sicher</span>
                    </div>

                    <div className="psg-rules">
                      <div className={`psg-rule ${passwordChecks.length ? 'met' : ''}`}>
                        <CheckIcon ok={passwordChecks.length} />
                        <span>Zwischen 8 und 72 Zeichen lang</span>
                      </div>
                      <div className={`psg-rule ${passwordChecks.lowercase ? 'met' : ''}`}>
                        <CheckIcon ok={passwordChecks.lowercase} />
                        <span>Kleinbuchstaben (a-z)</span>
                      </div>
                      <div className={`psg-rule ${passwordChecks.uppercase ? 'met' : ''}`}>
                        <CheckIcon ok={passwordChecks.uppercase} />
                        <span>Großbuchstaben (A-Z)</span>
                      </div>
                      <div className={`psg-rule ${passwordChecks.number ? 'met' : ''}`}>
                        <CheckIcon ok={passwordChecks.number} />
                        <span>Ziffern (0-9)</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="psg-input-wrap">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Passwort bestätigen"
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                    autoComplete="new-password"
                    disabled={submitting}
                  />
                  <button
                    className="psg-password-toggle"
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    disabled={submitting}
                    aria-label={showConfirm ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  >
                    <EyeIcon visible={showConfirm} />
                  </button>
                </div>

                {error && <p className="psg-error">{error}</p>}

                <button type="submit" className="psg-primary" disabled={submitting || password.length === 0}>
                  {submitting ? 'Speichern …' : 'Passwort speichern'}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {successOpen && (
        <>
          <div className="psg-overlay" />

          <div className="psg-modal psg-modal--success" role="dialog" aria-modal="true" aria-labelledby="psg-success-title" onClick={stop}>
            <div className="psg-head">
              <div className="psg-success-icon">✓</div>
              <h3 id="psg-success-title" className="psg-title">Passwort gespeichert</h3>
              <p className="psg-subtitle">
                Dein Passwort wurde erfolgreich festgelegt. Du kannst dich ab sofort auch
                direkt mit deiner E-Mail-Adresse und diesem Passwort anmelden.
              </p>
            </div>

            <div className="psg-body">
              <button type="button" className="psg-primary" onClick={() => setSuccessOpen(false)}>
                Verstanden
              </button>
            </div>
          </div>
        </>
      )}

      <style jsx global>{`
        .psg-overlay {
          position: fixed;
          inset: 0;
          z-index: 9998;
          background: rgba(0, 0, 0, 0.55);
        }

        .psg-modal {
          position: fixed;
          top: 50%;
          left: 50%;
          z-index: 9999;
          width: min(420px, 92vw);
          max-height: 90vh;
          overflow-y: auto;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          transform: translate(-50%, -50%);
          animation: psg-pop 0.22s ease-out;
        }

        .psg-head {
          padding: 40px 28px 26px;
          text-align: center;
          background: #eaf3fb;
        }

        .psg-icon {
          display: block;
          width: 48px;
          height: 48px;
          margin: 0 auto 14px;
        }

        .psg-success-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          margin: 0 auto 14px;
          color: #1a7f4b;
          font-size: 24px;
          font-weight: 700;
          background: #ffffff;
          border-radius: 50%;
        }

        .psg-title {
          margin: 0 0 8px;
          color: #1a1a1a;
          font-size: 24px;
          font-weight: 700;
          line-height: 1.2;
        }

        .psg-subtitle {
          margin: 0;
          color: #4a5565;
          font-size: 14px;
          line-height: 1.4;
        }

        .psg-body {
          padding: 24px 28px 28px;
        }

        .psg-input-wrap {
          position: relative;
          margin-bottom: 12px;
        }

        .psg-input-wrap input {
          width: 100%;
          padding: 12px 44px 12px 14px;
          border: 1.5px solid #cfd8e3;
          border-radius: 8px;
          font-size: 15px;
        }

        .psg-input-wrap input:focus {
          outline: none;
          border-color: #0f4c8a;
        }

        .psg-password-toggle {
          position: absolute;
          top: 50%;
          right: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          color: #667085;
          background: transparent;
          border: 0;
          transform: translateY(-50%);
          cursor: pointer;
        }

        .psg-password-toggle:hover {
          color: #0f4c8a;
        }

        .psg-password-toggle:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .psg-strength {
          margin: -4px 0 14px;
        }

        .psg-strength-label {
          margin-bottom: 4px;
          color: #333;
          font-size: 12px;
          font-weight: 600;
        }

        .psg-strength-bar {
          width: 100%;
          height: 6px;
          overflow: hidden;
          background: #e5e5e5;
          border-radius: 3px;
        }

        .psg-strength-bar-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.25s ease, background-color 0.25s ease;
        }

        .psg-strength-scale {
          display: flex;
          justify-content: space-between;
          margin-top: 4px;
          color: #666;
          font-size: 11px;
        }

        .psg-rules {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 10px;
        }

        .psg-rule {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #D82121;
          font-size: 12px;
          line-height: 1.2;
        }

        .psg-rule.met {
          color: #0e813a;
          font-weight: 700;
        }

        .psg-error {
          margin: 0 0 12px;
          color: #b11211;
          font-size: 13px;
          font-weight: 700;
        }

        .psg-primary {
          display: block;
          width: 100%;
          padding: 14px 20px;
          color: #fff;
          font-size: 15px;
          font-weight: 700;
          text-align: center;
          background: #0f4c8a;
          border: 0;
          border-radius: 999px;
          cursor: pointer;
        }

        .psg-primary:hover {
          background: #0c3d6f;
        }

        .psg-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .psg-primary:focus-visible {
          outline: 2px solid #0f4c8a;
          outline-offset: 2px;
        }

        .psg-modal--success .psg-body {
          padding-top: 4px;
        }

        @keyframes psg-pop {
          from {
            opacity: 0;
            transform: translate(-50%, -48%) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .psg-modal {
            animation: none;
          }
        }

        @media (max-width: 480px) {
          .psg-head {
            padding: 36px 20px 22px;
          }
          .psg-title {
            font-size: 21px;
          }
          .psg-body {
            padding: 20px 20px 24px;
          }
        }
      `}</style>
    </>,
    document.body
  );
}